import { hostBridge } from '@publicala/delfino';
import type { AIActions, AIFeedbackPayload, AIGeneratePayload, AIResponse } from '@publicala/delfino';
import { DEFAULT_AI_ACTIONS_MOCK, DEFAULT_AI_GENERATE_MOCK, isE2EMockActive } from '../../e2eMockGuard';

/**
 * AI Integrations API for Volpe.
 * Provides methods for AI actions (explain, expand, summarize) via hostBridge.
 */
class AIIntegrationsAPI {
    /**
     * Generate AI response for the given text and action.
     * Returns mock data in E2E standalone mode.
     *
     * @param payload - AI request parameters
     * @param payload.action - The AI action (explain, expand, summarize)
     * @param payload.text - The text to process
     * @param payload.lang - Optional language code
     * @returns AI response with data array and UUID for feedback
     */
    async generateResponse(payload: AIGeneratePayload): Promise<AIResponse> {
        if (isE2EMockActive()) {
            return DEFAULT_AI_GENERATE_MOCK;
        }
        return hostBridge.ai.generate(payload);
    }

    /**
     * Send feedback for an AI response.
     * Skipped in E2E standalone mode.
     *
     * @param payload - Feedback parameters
     * @param payload.uuid - The UUID of the AI response
     * @param payload.score - The feedback score (1 = good, 0 = bad)
     */
    async sendFeedback(payload: AIFeedbackPayload): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.ai.feedback(payload);
    }

    /**
     * List available AI actions.
     * Returns mock data in E2E standalone mode.
     *
     * @returns Map of action IDs to labels
     */
    async listActions(): Promise<AIActions> {
        if (isE2EMockActive()) {
            return DEFAULT_AI_ACTIONS_MOCK;
        }
        return hostBridge.ai.listActions();
    }
}

export const aiIntegrationsAPI = new AIIntegrationsAPI();
